import { motion, useReducedMotion } from 'motion/react';
import { cn } from '@/lib/cn';

type ClipImageProps = {
  src: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  priority?: boolean;
  /** Inset the curtain starts from. */
  inset?: number;
};

/**
 * Image that "unmasks" on scroll into view — the frame opens via clip-path
 * while the photo eases back from a slight zoom. A signature cinematic reveal.
 */
export function ClipImage({
  src,
  alt,
  className,
  imgClassName,
  priority = false,
  inset = 14,
}: ClipImageProps) {
  const reduce = useReducedMotion();

  if (reduce) {
    return (
      <div className={cn('relative overflow-hidden bg-paper-deep', className)}>
        <img src={src} alt={alt} className={cn('h-full w-full object-cover', imgClassName)} />
      </div>
    );
  }

  return (
    <motion.div
      className={cn('relative overflow-hidden bg-paper-deep', className)}
      initial={{ clipPath: `inset(${inset}% ${inset}% ${inset}% ${inset}%)` }}
      whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        initial={{ scale: 1.25 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true, amount: 0.35 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        className={cn('h-full w-full object-cover will-change-transform', imgClassName)}
      />
    </motion.div>
  );
}
